/**
 * An abstract class representing a script loader.
 * Subclasses must implement the specified methods and properties.
 */
export abstract class AbstractScriptLoader {
  /**
   * A property that should hold the name of the script loader.
   * Subclasses must provide their own implementation of this property.
   */
  abstract name: string;

  /**
   * An abstract method to determine whether the script is loaded.
   * Subclasses must implement this method to return a boolean value.
   * @returns {boolean} - True if the script is loaded; otherwise, false.
   */
  abstract isLoaded(): boolean;

  /**
   * An abstract method to load the script asynchronously.
   * Subclasses must implement this method and return a Promise<boolean>.
   * @param {string} trackingId - An optional tracking ID for the script.
   * @returns {Promise<boolean>} - A promise that resolves to true if the script is loaded successfully.
   */
  abstract load(trackingId?: string): Promise<boolean>;

  /**
   * An abstract method to initialize the script.
   * Subclasses must implement this method and return a Promise<boolean>.
   * @param {boolean} initNeeded - Indicates whether initialization is needed.
   * @param {string} trackingId - An optional tracking ID for the script.
   * @returns {Promise<boolean>} - A promise that resolves to true if initialization is successful.
   */
  abstract init(initNeeded: boolean, trackingId?: string): Promise<boolean>;
}
